/**
 * RateCardCard.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Displays and edits Project.pricing — one section per property type.
 *
 * Props:
 *   project    — project object with project.pricing dict
 *   onSave     — fn({ pricing }) → called with full updated pricing dict
 *   isSaving   — bool, disables Save while API call in-flight
 *
 * project.pricing shape:
 * {
 *   "apartment":   [{ label, slug, value, note, order }, ...],
 *   "retail_shop": [...],
 *   "warehouse":   [...],
 * }
 *
 * Renders nothing when project.pricing is empty (no configurations yet).
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React, { useState, useEffect } from 'react';
import { Box, Card, Typography, TextField, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import PercentIcon from '@mui/icons-material/Percent';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { INVENTORY_COLORS } from '../../../../utils/inventory_helper';

// ── Helpers ───────────────────────────────────────────────────────────────────

export function formatINRShort(val) {
  const n = parseFloat(val);
  if (val === '' || val == null || isNaN(n)) return '—';
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2).replace(/\.?0+$/, '')} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2).replace(/\.?0+$/, '')} L`;
  return `₹${n.toLocaleString('en-IN')}`;
}

// Labels that represent percentages (not rupees)
const PERCENT_LABELS = new Set(['gst', 'stamp_duty', 'registration']);

export function isPercentField(label) {
  return PERCENT_LABELS.has(label);
}

export function typeSlugToLabel(slug) {
  if (!slug) return '';
  return slug.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function rowSlugToLabel(slug) {
  if (!slug) return '';
  return slug.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Atoms ─────────────────────────────────────────────────────────────────────

/** Pill tab for switching between property types */
export const TypeTab = ({ label, active, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      px: 1.5, py: 0.45,
      borderRadius: 2,
      border: '1px solid',
      fontSize: '0.75rem',
      fontWeight: 600,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      transition: 'all 0.15s',
      bgcolor: active ? INVENTORY_COLORS.green : 'transparent',
      color: active ? '#fff' : 'text.secondary',
      borderColor: active ? INVENTORY_COLORS.green : 'divider',
      '&:hover': active ? {} : {
        borderColor: INVENTORY_COLORS.green,
        color: INVENTORY_COLORS.green,
      },
    }}
  >
    {label}
  </Box>
);

/** Single rate card row — read or edit */
export const RateRow = ({ row, isEditing, draftValue, onChange }) => {
  const isPct = isPercentField(row.slug ?? row.label);
  const hasValue = row.value !== '' && row.value != null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        py: 1.1,
        borderRadius: 1.25,
        bgcolor: 'grey.50',
        border: '1px solid',
        borderColor: isEditing ? 'grey.200' : 'grey.100',
        transition: 'border-color 0.15s',
      }}
    >
      {/* Icon */}
      <Box sx={{
        width: 28, height: 28, borderRadius: 1, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '1px solid',
        bgcolor: isPct ? 'rgba(106,27,154,0.07)' : INVENTORY_COLORS.greenLight,
        borderColor: isPct ? 'rgba(106,27,154,0.18)' : `${INVENTORY_COLORS.greenAccent}40`,
      }}>
        {isPct
          ? <PercentIcon sx={{ fontSize: 13, color: '#6a1b9a' }} />
          : <CurrencyRupeeIcon sx={{ fontSize: 13, color: INVENTORY_COLORS.green }} />
        }
      </Box>

      {/* Label + optional note tooltip */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
            {row.label || rowSlugToLabel(row.slug)}
          </Typography>
          {row.note && (
            <Tooltip title={row.note} placement="top" arrow>
              <InfoOutlinedIcon sx={{ fontSize: 12, color: 'text.disabled', cursor: 'help', flexShrink: 0 }} />
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Value */}
      {isEditing ? (
        <TextField
          size="small"
          type="number"
          value={draftValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder={isPct ? 'e.g. 5' : 'e.g. 25000'}
          inputProps={{ min: 0, step: isPct ? 0.1 : 1 }}
          InputProps={isPct
            ? { endAdornment: <Typography sx={{ fontSize: 11, color: 'text.secondary', pr: 0.5 }}>%</Typography> }
            : { startAdornment: <Typography sx={{ fontSize: 11, color: 'text.secondary', pl: 0.25, pr: 0.25 }}>₹</Typography> }
          }
          sx={{ width: 120, '& .MuiInputBase-root': { height: 30, fontSize: 12, borderRadius: 1 } }}
        />
      ) : (
        <Typography sx={{
          fontSize: '0.8125rem',
          fontWeight: 700,
          color: hasValue ? (isPct ? '#6a1b9a' : INVENTORY_COLORS.green) : 'text.disabled',
          minWidth: 68,
          textAlign: 'right',
          whiteSpace: 'nowrap',
        }}>
          {hasValue
            ? isPct ? `${row.value}%` : formatINRShort(row.value)
            : '—'
          }
        </Typography>
      )}
    </Box>
  );
};

/** Completeness badge */
export const FillProgress = ({ filled, total }) => {
  const complete = filled === total && total > 0;
  return (
    <Typography sx={{
      fontSize: '0.6875rem',
      fontWeight: 700,
      color: complete ? INVENTORY_COLORS.green : 'text.disabled',
    }}>
      {filled}/{total} filled
    </Typography>
  );
};

/** Card header with Edit / Save / Cancel */
export const RateCardHeader = ({ isEditing, onEdit, onSave, onCancel, saveDisabled }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    px: 2.75, py: 1.75, borderBottom: '1px solid', borderColor: 'divider',
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{
        width: 28, height: 28, borderRadius: '50%',
        bgcolor: INVENTORY_COLORS.greenLight,
        border: '1px solid', borderColor: `${INVENTORY_COLORS.greenAccent}cc`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <CurrencyRupeeIcon sx={{ fontSize: 15, color: INVENTORY_COLORS.green }} />
      </Box>
      <Typography variant="overline" fontWeight={700}
        sx={{ letterSpacing: 1, fontSize: '0.6875rem', color: 'text.secondary' }}>
        Rate Card
      </Typography>
    </Box>

    {isEditing ? (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        <Box component="button" type="button" onClick={onCancel} sx={{
          display: 'inline-flex', alignItems: 'center', px: 1.25, py: 0.5,
          borderRadius: 2.5, border: '1px solid', borderColor: 'grey.300',
          fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
          bgcolor: 'transparent', color: 'text.secondary',
          '&:hover': { bgcolor: 'grey.100' },
        }}>Cancel</Box>
        <Box component="button" type="button" onClick={onSave} disabled={saveDisabled} sx={{
          display: 'inline-flex', alignItems: 'center', px: 1.25, py: 0.5,
          borderRadius: 2.5, border: 'none',
          fontSize: '0.75rem', fontWeight: 600,
          cursor: saveDisabled ? 'not-allowed' : 'pointer',
          bgcolor: INVENTORY_COLORS.green, color: '#fff',
          opacity: saveDisabled ? 0.7 : 1,
          '&:hover': saveDisabled ? {} : { bgcolor: INVENTORY_COLORS.greenMid },
        }}>Save</Box>
      </Box>
    ) : (
      <Box component="button" type="button" onClick={onEdit} sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5,
        px: 1.25, py: 0.5, borderRadius: 2.5,
        border: '1px solid', borderColor: 'grey.300',
        bgcolor: 'grey.100', color: 'text.primary',
        fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
        '&:hover': { bgcolor: 'grey.200' },
      }}>
        <EditIcon sx={{ fontSize: 13 }} /> Edit
      </Box>
    )}
  </Box>
);

/** Hint shown when some rows are still empty */
export const EmptyRowsHint = ({ emptyCount }) => {
  if (emptyCount <= 0) return null;
  return (
    <Box sx={{
      mx: 2, mb: 2, p: 1.25, borderRadius: 1.25,
      bgcolor: INVENTORY_COLORS.greenLight,
      border: '1px solid', borderColor: `${INVENTORY_COLORS.greenAccent}40`,
    }}>
      <Typography sx={{ fontSize: '0.6875rem', color: INVENTORY_COLORS.green, lineHeight: 1.6 }}>
        <strong>{emptyCount}</strong> field{emptyCount > 1 ? 's' : ''} not yet filled.
        Click <strong>Edit</strong> to add values — your team uses these when quoting.
      </Typography>
    </Box>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

/** Normalize pricing[type]: API may return dict { slug: value }; we need array of { slug, label?, value } */
function normalizePricingByType(pricing) {
  if (!pricing || typeof pricing !== 'object') return {};
  const out = {};
  Object.entries(pricing).forEach(([typeSlug, entry]) => {
    if (Array.isArray(entry)) {
      out[typeSlug] = entry.map((r) => ({ ...r, label: r.label ?? rowSlugToLabel(r.slug) }));
    } else if (entry && typeof entry === 'object') {
      out[typeSlug] = Object.entries(entry).map(([slug, value]) => ({
        slug,
        label: rowSlugToLabel(slug),
        value: value ?? '',
      }));
    } else {
      out[typeSlug] = [];
    }
  });
  return out;
}

/** Build category -> [slug] from project.pricing keys and propertyTypes (optional) */
function groupPricingByCategory(pricing, propertyTypes = []) {
  const slugToCategory = {};
  if (Array.isArray(propertyTypes) && propertyTypes.length > 0) {
    propertyTypes.forEach((pt) => {
      const slug = (pt.slug || pt.name || '').toString().toLowerCase().replace(/\s+/g, '_');
      if (slug) slugToCategory[slug] = pt.category_name || pt.category || 'Other';
    });
  }
  const byCategory = {};
  Object.keys(pricing || {}).forEach((slug) => {
    const cat = slugToCategory[slug] || 'Other';
    if (!byCategory[cat]) byCategory[cat] = [];
    byCategory[cat].push(slug);
  });
  return byCategory;
}

/**
 * RateCardCard
 *
 * Props:
 *   project       — project object with project.pricing dict (keyed by property_type slug)
 *   onSave        — fn({ pricing }) → called with full updated pricing dict
 *   isSaving      — bool
 *   propertyTypes — optional; from inventory options; used to group rate card by category
 */
const PriceRateCard = ({ project, onSave, isSaving = false, propertyTypes }) => {
  const rawPricing = project?.pricing ?? {};
  const pricing = normalizePricingByType(rawPricing);
  const allTypes = Object.keys(pricing);
  const byCategory = groupPricingByCategory(pricing, propertyTypes);
  const categories = Object.keys(byCategory).sort();
  const useCategories = categories.length > 1;

  const [activeCategory, setActiveCategory] = useState(null);
  const [activeType, setActiveType] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState({});

  const typesInScope = useCategories && activeCategory ? (byCategory[activeCategory] || []) : allTypes;

  useEffect(() => {
    if (!allTypes.length) return;
    if (useCategories && categories.length && !activeCategory) setActiveCategory(categories[0]);
    if (!useCategories && (!activeType || !pricing[activeType])) setActiveType(allTypes[0]);
    if (useCategories && activeCategory) {
      const inCat = byCategory[activeCategory] || [];
      if (!activeType || !inCat.includes(activeType)) setActiveType(inCat[0] || null);
    }
  }, [allTypes.join(','), activeCategory, useCategories, categories.join(',')]);

  useEffect(() => {
    if (isEditing && activeType) {
      const rows = pricing[activeType] ?? [];
      setDraft(Object.fromEntries(rows.map((r) => [r.slug, r.value ?? ''])));
    }
  }, [isEditing, activeType]);

  if (!allTypes.length) return null;

  const rows = pricing[activeType] ?? [];
  const filledCount = rows.filter((r) => r.value !== '' && r.value != null).length;

  const handleTypeChange = (slug) => {
    setIsEditing(false);
    setDraft({});
    setActiveType(slug);
  };

  const handleCategoryChange = (cat) => {
    setIsEditing(false);
    setDraft({});
    setActiveCategory(cat);
    const first = (byCategory[cat] || [])[0];
    setActiveType(first || null);
  };

  const handleSave = () => {
    const updatedPricing = {
      ...pricing,
      [activeType]: rows.map((r) => ({
        ...r,
        value: draft[r.slug] !== undefined ? draft[r.slug] : r.value,
      })),
    };
    onSave?.({ pricing: updatedPricing });
    setIsEditing(false);
    setDraft({});
  };

  const handleCancel = () => {
    setIsEditing(false);
    setDraft({});
  };

  const updateDraft = (slug, val) =>
    setDraft((prev) => ({ ...prev, [slug]: val }));

  return (
    <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>

      <RateCardHeader
        isEditing={isEditing}
        onEdit={() => setIsEditing(true)}
        onSave={handleSave}
        onCancel={handleCancel}
        saveDisabled={isSaving}
      />

      {/* Category tabs — when propertyTypes provided and 2+ categories */}
      {useCategories && (
        <Box sx={{
          display: 'flex', gap: 0.75, px: 2, pt: 1.5, pb: 1,
          flexWrap: 'wrap', borderBottom: '1px solid', borderColor: 'divider',
        }}>
          {categories.map((cat) => (
            <TypeTab
              key={cat}
              label={cat}
              active={activeCategory === cat}
              onClick={() => handleCategoryChange(cat)}
            />
          ))}
        </Box>
      )}

      {/* Type tabs within category */}
      {typesInScope.length > 1 && (
        <Box sx={{
          display: 'flex', gap: 0.75, px: 2, pt: 1.5, pb: 1,
          flexWrap: 'wrap', borderBottom: '1px solid', borderColor: 'divider',
        }}>
          {typesInScope.map((slug) => (
            <TypeTab
              key={slug}
              label={typeSlugToLabel(slug)}
              active={activeType === slug}
              onClick={() => handleTypeChange(slug)}
            />
          ))}
        </Box>
      )}

      <Box sx={{ px: 2, pt: 1.5, pb: 0.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
          {useCategories && activeCategory ? `${activeCategory} · ` : ''}
          {typeSlugToLabel(activeType)} · shared across configs of this type
        </Typography>
        <FillProgress filled={filledCount} total={rows.length} />
      </Box>

      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {rows.length === 0 ? (
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', textAlign: 'center', py: 2 }}>
            No rate card fields defined for this type.
          </Typography>
        ) : (
          rows.map((row) => (
            <RateRow
              key={row.slug}
              row={row}
              isEditing={isEditing}
              draftValue={draft[row.slug] ?? ''}
              onChange={(val) => updateDraft(row.slug, val)}
            />
          ))
        )}
      </Box>

      {!isEditing && (
        <EmptyRowsHint emptyCount={rows.length - filledCount} />
      )}
    </Card>
  );
};

export default PriceRateCard;
